import { Component, OnInit, Output, EventEmitter, OnDestroy, ChangeDetectorRef, Injector, Input } from '@angular/core';
import { HttpHelpService } from '../../../Data/service/http-help.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { VatType } from '../../models/vat.model';
import { BaseControlComponent } from '../base-control/base-control.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

/**
 * Dropdown component for selecting a VAT type.
 * Integrates with Angular Reactive Forms and PrimeNG.
 * Fetches VAT types from the backend and handles validation.
 */
@Component({
	selector: 'app-vat-select',
	templateUrl: './vat-select.component.html',
	styleUrls: ['./vat-select.component.scss']
})
export class VatSelectComponent extends BaseControlComponent implements OnInit, OnDestroy {

	/**
	 * Emits the selected VAT ID when the selection changes.
	 */
	@Output() vatTypeChange = new EventEmitter<number | null>();

	private _vatTypesList: VatType[] = [];
	/**
	 * List of VAT types to display in the dropdown.
	 */
	set vatTypesList(val: VatType[]) {
		this._vatTypesList = Array.isArray(val) ? val : [];
	}
	get vatTypesList(): VatType[] {
		return this._vatTypesList || [];
	}

	/**
	 * The current language setting for localization.
	 */
	@Input() curentLang: string = localStorage.getItem('curentLang') || 'en';
	/**
	 * Flag indicating if VAT data is currently being loaded.
	 */
	isLoading = false;

	/**
	 * Enables debug logging for this component instance.
	 */
	@Input() debug: boolean = false;

	constructor(
		private service: HttpHelpService,
		private spinner: NgxSpinnerService,
		protected override injector: Injector,
		protected override messageService: MessageService,
		protected override translate: TranslateService,
		protected override cdr: ChangeDetectorRef
	) {
		super(injector, translate, cdr, messageService);
	}

	/**
	 * Initializes the component, loads VAT types, and subscribes to parent form changes.
	 */
	override ngOnInit(): void {
		super.ngOnInit();
		if (this.control) {
			this.getVatTypes();
			this.parentValueSub = this.control.valueChanges.subscribe(value => {
this.writeValue(value);
			});
		}
	}

	/**
	 * Cleans up subscriptions on destroy.
	 */
	ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	/**
	 * Loads the list of active VAT types from the backend service.
	 * Sets a default value if the control is empty and handles validation.
	 */
	getVatTypes(): void {
		if (this.isLoading) return;
		this.isLoading = true;
		this.spinner.show();
		this.service.getAllActiveVatList().subscribe({
			next: (res: VatType[]) => {
				this.vatTypesList = res || [];
let valueToSet = this.control?.value;
				if (
					this.vatTypesList.length > 0 &&
					(valueToSet === null || valueToSet === undefined || valueToSet === '')
				) {
					valueToSet = this.vatTypesList[0].vat_list_id;
					this.control?.setValue(valueToSet, { emitEvent: false });
				}
				setTimeout(() => {
					this.writeValue(valueToSet);
					if (this.debug) {
						const selectedObj = this.vatTypesList.find(v => v.vat_list_id === valueToSet);
						}
					this.cdr.detectChanges();
				});
				if (this.vatTypesList.length === 0 && this.required && this.control) {
					this.control.setErrors({ required: true });
					this.control.markAsTouched();
					this.control.markAsDirty();
					if (this.debug) {
						console.warn('[DEBUG/VatSelect] No VAT types and required: setErrors, markAsTouched/Dirty');
						}
				}
				this.isLoading = false;
				this.spinner.hide();
				this.cdr.markForCheck();
			},
			error: () => {
				this.vatTypesList = [];
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('shared.vatSelect.loadErrorSummary'),
					detail: this.translate.instant('shared.vatSelect.loadErrorDetail')
				});
				if (this.required && this.control) {
					this.control.setErrors({ required: true });
					this.control.markAsTouched();
					this.control.markAsDirty();
					if (this.debug) {
						console.warn('[DEBUG/VatSelect] API error and required: setErrors, markAsTouched/Dirty');
						}
				}
				this.isLoading = false;
				this.spinner.hide();
				this.cdr.markForCheck();
			}
		});
	}

	/**
	 * Handles selection changes in the dropdown.
	 * Emits the selected VAT ID and marks the control as interacted.
	 * @param selectedId The ID of the selected VAT, or null if cleared.
	 */
	onVatSelectionChange(selectedId: number | null): void {
		if (selectedId == null) return;
this.propagateChange(selectedId);
		this.vatTypeChange.emit(selectedId);
		this.markAsInteracted();
	}

	/**
	 * Called when the dropdown loses focus (blur event).
	 * Ensures the control is marked as touched and validation is updated.
	 */
	onDropdownBlur(): void {
		this.propagateTouched();
		if (this.required && this.control) {
			const isEmpty = this.control.value === null || this.control.value === '' || typeof this.control.value === 'undefined';
			if (isEmpty) {
				this.control.setValue(null, { emitEvent: false });
				this.control.setErrors({ required: true });
				this.control.markAsTouched();
				this.control.markAsDirty();
			}
		}
		if (this.vatTypesList.length === 0 && this.required && this.control) {
			this.control.setErrors({ required: true });
			this.control.markAsTouched();
			this.control.markAsDirty();
		}
		(this as any)._updateDisplayValidationState();
		if (this.control) {
			this.control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		}
		this.cdr.markForCheck();
	}

	/**
	 * Returns the property name for the VAT name based on the current language.
	 */
	getVatNameProperty(): string {
		switch (this.curentLang) {
			case 'ar': return 'vat_name_ar';
			case 'he': return 'vat_name_he';
			default: return 'vat_name_en';
		}
	}

	/**
	 * Retrieves the VAT name in the appropriate language for display.
	 * @param item The VAT object.
	 * @returns The localized VAT name, or an empty string if item is null.
	 */
	getVatName(item: VatType | null): string {
		if (!item) return '';
		switch (this.curentLang) {
			case 'ar': return item.vat_name_ar || item.vat_name_he || item.vat_name_en || '';
			case 'he': return item.vat_name_he || item.vat_name_en || '';
			default: return item.vat_name_en || item.vat_name_he || '';
		}
	}
}